import React from "react"
import { Col, Row, Table } from "react-bootstrap"
import { useSelector } from "react-redux"

import FormikBootstrap from "@/components/formik-bootstrap"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import Pricing from "@/pricing"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"
import { ReferencedValueTypes } from "@/onboarding/features/product/models/Product"
import RateInputs from "@/onboarding/features/product/components/shared/RateInputs"
import ComponentOperatorSelect from "@/onboarding/features/product/components/shared/ComponentOperatorSelect"
import { templateRenders } from "@/onboarding/features/product/selectors"

import OperatorDisplay from "./OperatorDisplay"
import TenderStatus from "./TenderStatus"

const WriteView = ({
  hideRates = false,
  showOperator = false,
  referencedValueMessage = "",
  tender = null,
}): JSX.Element => {
  const showSpecialOpeningRequest = useSelector(templateRenders("venue", "special_opening_request"))

  return (
    <div className="d-flex flex-column gap-2">
      <Row>
        <Col md={8} className="d-flex flex-column gap-2">
          <h5 className="mb-0">
            Edit Details
            <TenderStatus tender={tender} />
          </h5>
          <Row>
            <Col md={5}>
              {/*
              The name field is required.
              There is an open feature request for extracting requiredness from the schema: https://github.com/jaredpalmer/formik/issues/1241
            */}
              <FormikBootstrap.TextInput name="name" label="Name *" />
            </Col>
            <Col md={5}>
              <ReferencedValueSelect
                name="venueType"
                type={ReferencedValueTypes.VENUE_TYPE}
                label="Venue Type"
                emptyMessage={referencedValueMessage}
              />
            </Col>
          </Row>
          <Row>
            <Col md={5}>
              <FormikBootstrap.NumberInput name="steps" label="Steps" />
            </Col>
            <Col md={5}>
              <FormikBootstrap.NumberInput
                name="distanceFromParking"
                label="Distance From Parking (meters)"
              />
            </Col>
          </Row>
          {showOperator && (
            <Row>
              <Col md={10}>
                <ComponentOperatorSelect />
              </Col>
            </Row>
          )}
          <Row>
            <Col md={10}>
              <FormikBootstrap.TextInput name="description" label="Description" />
            </Col>
          </Row>
          <Row>
            <Col md={5}>
              <FormikBootstrap.Checkbox
                name="wheelchairAccessible"
                label="Wheelchair Accessible?"
              />
            </Col>
            <Col md={5}>
              <FormikBootstrap.Checkbox name="restroomsAvailable" label="Restrooms Available?" />
            </Col>
          </Row>
          {showSpecialOpeningRequest && (
            <Row>
              <Col md={10}>
                <FormikBootstrap.Checkbox
                  name="specialOpeningRequest"
                  label="Special Opening Request Possible?"
                />
              </Col>
            </Row>
          )}
        </Col>
        {!hideRates && (
          <Col md={4} className="ms-n4">
            <h5 className="mt-0">Edit Rates</h5>
            <RateInputs name="ratesheet" />
          </Col>
        )}
      </Row>
    </div>
  )
}

const ReadView = ({
  component,
  hideRates,
  showOperator,
  tender,
  previousValues = false,
}): JSX.Element => {
  const textStyle = previousValues ? "original-product-text" : ""
  const showSpecialOpeningRequest = useSelector(templateRenders("venue", "special_opening_request"))

  return (
    <div className="d-flex flex-column gap-2">
      <Row className="d-flex">
        <Col md={hideRates ? 9 : 6}>
          <h5 className={textStyle}>
            Details
            <TenderStatus tender={tender} />
          </h5>
          <Table>
            <thead>
              <tr>
                <th className={textStyle}>Name</th>
                <th className={textStyle}>Type</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td aria-label="Name">
                  <TextDisplay value={component.name} previousValue={previousValues} />
                </td>
                <td aria-label="Venue Type">
                  <TextDisplay value={component.venueType} previousValue={previousValues} />
                </td>
              </tr>
            </tbody>
            <thead>
              <tr>
                <th className={textStyle}>Steps</th>
                <th className={textStyle}>Distance From Parking (meters)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td aria-label="Steps">
                  <TextDisplay value={component.steps} previousValue={previousValues} />
                </td>
                <td aria-label="Distance From Parking (meters)">
                  <TextDisplay
                    value={component.distanceFromParking}
                    previousValue={previousValues}
                  />
                </td>
              </tr>
            </tbody>
            {showOperator && (
              <>
                <thead>
                  <tr>
                    <th className={textStyle} colSpan={2}>
                      Operator
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td aria-label="Operator" colSpan={2}>
                      <OperatorDisplay
                        operatorId={component.operatorId}
                        previousValue={previousValues}
                      />
                    </td>
                  </tr>
                </tbody>
              </>
            )}
            <thead>
              <tr>
                <th className={textStyle} colSpan={2}>
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td aria-label="Description" colSpan={2}>
                  <TextDisplay value={component.description} previousValue={previousValues} />
                </td>
              </tr>
            </tbody>
            <thead>
              <tr>
                <th className={textStyle}>Wheelchair Accessible?</th>
                <th className={textStyle}>Restrooms Available?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td aria-label="Wheelchair Accessible?">
                  <TextDisplay
                    value={component.wheelchairAccessible}
                    previousValue={previousValues}
                  />
                </td>
                <td aria-label="Restrooms Available?">
                  <TextDisplay
                    value={component.restroomsAvailable}
                    previousValue={previousValues}
                  />
                </td>
              </tr>
            </tbody>
            {showSpecialOpeningRequest && (
              <>
                <thead>
                  <tr>
                    <th className={textStyle} colSpan={2}>
                      Special Opening Request Possible?
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td aria-label="Special Opening Request Possible?" colSpan={2}>
                      <TextDisplay
                        value={component.specialOpeningRequest}
                        previousValue={previousValues}
                      />
                    </td>
                  </tr>
                </tbody>
              </>
            )}
          </Table>
        </Col>
        {!hideRates && (
          <Col md={3} className="ms-5">
            <h5 className={textStyle}>Rates</h5>
            <Pricing.View value={component.ratesheet} previousValues={previousValues} />
          </Col>
        )}
      </Row>
    </div>
  )
}

const View = ({
  component,
  previousValues = false,
  editing = false,
  hideRates = false,
  showOperator = false,
  tender = null,
  referencedValueMessage = "",
}): JSX.Element =>
  editing ? (
    <WriteView
      hideRates={hideRates}
      showOperator={showOperator}
      referencedValueMessage={referencedValueMessage}
      tender={tender}
    />
  ) : (
    <ReadView
      previousValues={previousValues}
      component={component}
      hideRates={hideRates}
      showOperator={showOperator}
      tender={tender}
    />
  )

export default View
